import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/lib/axios'
import { useQueryTenantMe } from '../api/useQueryTenantMe'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

interface ConfirmResponse {
  activated: boolean
  pending: boolean
  failed: boolean
}

export const PaymentSuccessPage: React.FC = () => {
  const { t } = useLocalize()
  const qc = useQueryClient()
  const { data: tenant, refetch } = useQueryTenantMe()
  const [refreshCount, setRefreshCount] = React.useState(0)
  const [confirmedOnce, setConfirmedOnce] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  const sessionId = React.useMemo(
    () => new URLSearchParams(window.location.search).get('session_id'),
    [],
  )

  const ready = tenant?.paid && !tenant.permanentlyBlocked

  // After returning from Przelewy24 we finalize by sessionId: the server looks
  // up the order, verifies it with P24, and activates the tenant. This confirms
  // the payment locally (the async urlStatus notification can't reach a dev box)
  // and is idempotent in production (the webhook may win the race). We act on the
  // response: stop on `activated` (or terminal `failed`), and only keep polling
  // while `pending` — instead of blindly re-confirming on every tick.
  React.useEffect(() => {
    if (ready || failed) return
    if (refreshCount >= 6) return
    const t = setTimeout(async () => {
      if (tenant?.companyId && sessionId && !confirmedOnce) {
        try {
          const { data } = await axiosInstance.post<ConfirmResponse>(
            `/config-api/${tenant.companyId}/subscription/p24/confirm`,
            { sessionId },
          )
          if (data?.failed) {
            setFailed(true)
            return
          }
          if (data?.activated) {
            setConfirmedOnce(true) // stop re-confirming; just refetch tenant below
          }
        } catch {
          // transient — retry on the next tick / fall back to polling
        }
      }
      qc.invalidateQueries({ queryKey: ['tenant.me'] })
      qc.invalidateQueries({ queryKey: ['subscription.status'] })
      refetch()
      setRefreshCount((n) => n + 1)
    }, confirmedOnce ? 800 : 2000)
    return () => clearTimeout(t)
  }, [tenant, ready, failed, confirmedOnce, refreshCount, sessionId, qc, refetch])

  if (failed) {
    return (
      <div style={{ maxWidth: 480, margin: '96px auto', padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h1>{t('subscription.paymentSuccess.failedTitle')}</h1>
        <p style={{ color: '#6b7280', marginTop: 8 }}>
          {t('subscription.paymentSuccess.failedText')}
        </p>
        <a
          href="/checkout"
          style={{
            display: 'inline-block', marginTop: 24, padding: '12px 24px',
            background: '#2563eb', color: '#fff', textDecoration: 'none',
            borderRadius: 6, fontWeight: 600,
          }}
        >
          {t('subscription.paymentSuccess.backToPlans')}
        </a>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 480, margin: '96px auto', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{ready ? '✓' : '⏳'}</div>
      <h1>
        {ready
          ? t('subscription.paymentSuccess.confirmedTitle')
          : t('subscription.paymentSuccess.pendingTitle')}
      </h1>
      <p style={{ color: '#6b7280', marginTop: 8 }}>
        {ready
          ? t('subscription.paymentSuccess.confirmedText')
          : t('subscription.paymentSuccess.pendingText')}
      </p>
      {ready && tenant && (
        <a
          href={`/et/company/${tenant.companyId}/dashboard`}
          style={{
            display: 'inline-block',
            marginTop: 24,
            padding: '12px 24px',
            background: '#2563eb',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          {t('subscription.paymentSuccess.goToDashboard')}
        </a>
      )}
    </div>
  )
}
